import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShieldAlt, faInfoCircle, faFlag } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import InternalShell, { useInternalApi } from '../layouts/InternalShell';
import { COMPLIANCE_NAV, SEVERITY_COLORS, RULE_STATUS_META } from './nav';

interface Rule {
    key: string; title: string; framework: string; severity: string;
    status: string; detail: string; failing_count: number;
}
interface Summary {
    total_rules: number; passed: number; failed: number; not_assessable: number;
    score_pct: number | null; critical_failures: number;
}

const CompliancePosture: React.FC = () => {
    const { api } = useInternalApi();
    const [rules, setRules] = useState<Rule[]>([]);
    const [summary, setSummary] = useState<Summary | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => { document.title = 'Compliance Posture - Uzima Internal'; }, []);

    const load = async () => {
        setLoading(true);
        try {
            const res = await api.get('compliance-api/posture/');
            if (res.data?.status === 'success') {
                setRules(res.data.data.rules || []);
                setSummary(res.data.data.summary);
            }
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const raiseFlag = async (rule: Rule) => {
        const confirm = await Swal.fire({
            title: 'Raise a violation flag?',
            text: `This opens an investigable case for "${rule.title}".`,
            icon: 'question', showCancelButton: true, confirmButtonText: 'Raise flag',
        });
        if (!confirm.isConfirmed) return;
        try {
            await api.post('compliance-api/violations/', {
                title: rule.title, description: rule.detail,
                severity: rule.severity, rule_key: rule.key,
            });
            Swal.fire({ icon: 'success', title: 'Flag raised', timer: 1400, showConfirmButton: false });
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Could not raise flag.' });
        }
    };

    return (
        <InternalShell activeDashboard="compliance_center" subNav={COMPLIANCE_NAV}>
            <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faShieldAlt} className="me-2 text-primary" />Compliance Posture</h2>
            <p className="text-muted mb-4">Every rule below is evaluated live against production data each time this page loads</p>

            {loading || !summary ? (
                <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
            ) : (
                <>
                    <div className="row mb-4">
                        <div className="col-md-3 mb-3">
                            <div className="card border-0 shadow-sm"><div className="card-body">
                                <div className="text-muted small mb-1">Score (assessable rules)</div>
                                <h3 className={summary.score_pct === 100 ? 'text-success' : summary.score_pct && summary.score_pct >= 80 ? 'text-warning' : 'text-danger'}>
                                    {summary.score_pct !== null ? `${summary.score_pct}%` : '—'}
                                </h3>
                            </div></div>
                        </div>
                        <div className="col-md-3 mb-3">
                            <div className="card border-0 shadow-sm"><div className="card-body">
                                <div className="text-muted small mb-1">Passing</div><h3 className="text-success">{summary.passed}</h3>
                            </div></div>
                        </div>
                        <div className="col-md-3 mb-3">
                            <div className={`card border-0 shadow-sm ${summary.failed ? 'border-danger' : ''}`}><div className="card-body">
                                <div className="text-muted small mb-1">Failing</div>
                                <h3 className={summary.failed ? 'text-danger' : ''}>{summary.failed}</h3>
                            </div></div>
                        </div>
                        <div className="col-md-3 mb-3">
                            <div className="card border-0 shadow-sm"><div className="card-body">
                                <div className="text-muted small mb-1">Not assessable</div>
                                <h3 className="text-muted">{summary.not_assessable}</h3>
                            </div></div>
                        </div>
                    </div>

                    {summary.critical_failures > 0 && (
                        <div className="alert alert-danger">
                            <strong>{summary.critical_failures} critical rule(s) failing.</strong> These carry statutory notification or agreement obligations.
                        </div>
                    )}

                    {summary.not_assessable > 0 && (
                        <div className="alert alert-secondary small">
                            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                            The score covers only rules that can currently be evaluated. {summary.not_assessable} rule(s) lack the underlying records to assess and are excluded rather than counted as passing — an incomplete picture is never shown as a clean bill of health.
                        </div>
                    )}

                    <div className="card border-0 shadow-sm">
                        <div className="card-body p-0">
                            <table className="table table-hover align-middle mb-0">
                                <thead className="table-light"><tr><th>Rule</th><th>Framework</th><th>Severity</th><th>Status</th><th>Finding</th><th></th></tr></thead>
                                <tbody>
                                    {rules.map(r => (
                                        <tr key={r.key} className={r.status === 'fail' ? 'table-danger' : ''}>
                                            <td><strong>{r.title}</strong></td>
                                            <td className="small text-muted">{r.framework}</td>
                                            <td><span className={`badge bg-${SEVERITY_COLORS[r.severity]}`}>{r.severity}</span></td>
                                            <td><span className={`badge bg-${RULE_STATUS_META[r.status]?.color}`}>{RULE_STATUS_META[r.status]?.label}</span></td>
                                            <td className="small">{r.detail}</td>
                                            <td>
                                                {r.status === 'fail' && (
                                                    <button className="btn btn-sm btn-outline-danger" onClick={() => raiseFlag(r)}>
                                                        <FontAwesomeIcon icon={faFlag} className="me-1" />Flag
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}
        </InternalShell>
    );
};

export default CompliancePosture;
